import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CommunityGamesShell from "./CommunityGamesShell";
import GameShare from "./GameShare";
import { fetchGame, startPlay } from "./gamesApi";
import { gameTypeFor, levelPath, selectLevel } from "./gameTypes";

/**
 * Community → Games → one game (published only; unknown or unpublished = not found).
 * Native games list their levels; ?level=<id> selects one (only that level's
 * content / image is loaded). A missing or no-longer-available level shows a
 * message with the level list.
 */
function GamePlayPage(props) {
  const slug =
    (props.match && props.match.params && props.match.params.slug) || "";
  const search = (props.location && props.location.search) || "";
  const [game, setGame] = useState(null);
  const [status, setStatus] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [play, setPlay] = useState({ token: null, round: 0, error: null, data: null });
  const [completed, setCompleted] = useState(null);
  const roundRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setGame(null);
    fetchGame(slug).then((result) => {
      if (cancelled) return;
      if (result.game) {
        setGame(result.game);
        setStatus("ready");
      } else {
        setStatus(result.notFound ? "not_found" : "error");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadKey]);

  const type = game ? gameTypeFor(game.type) : null;
  const levels = game && Array.isArray(game.levels) ? game.levels : [];
  const selection = type && type.hasLevels ? selectLevel(levels, search) : { level: null, invalid: false };
  const level = selection.level;
  const levelId = level ? level.id : null;

  // Every new round (level opened, restart, difficulty change) gets a fresh signed play session.
  const handleRoundStart = useCallback(() => {
    if (!type || !type.shareable || !levelId) return;
    roundRef.current += 1;
    const round = roundRef.current;
    setCompleted(null);
    setPlay({ token: null, round, error: null, data: null });
    startPlay(slug, levelId).then((result) => {
      if (roundRef.current !== round) return;
      setPlay({ token: result.ok ? result.data.token : null, round, error: result.ok ? null : result.error, data: result.ok ? result.data : null });
    });
  }, [slug, levelId, type]);

  // Jigsaw / Word Search pass their solution proof; Trivia / Whack-a-Mole pass the server-verified completion (null when signed out).
  const handleComplete = useCallback((outcome) => {
    if (outcome && Object.prototype.hasOwnProperty.call(outcome, "completion")) {
      setCompleted({ completion: outcome.completion, round: roundRef.current });
    } else {
      setCompleted({ proof: outcome, round: roundRef.current });
    }
  }, []);

  let player = null;
  if (game && type) {
    if (type.hasLevels) {
      player = level ? type.render(game, level) : null;
    } else {
      player = type.render(game);
    }
  }

  const backLink = (
    <p className="games-back">
      <Link to="/games">← Back to Games</Link>
    </p>
  );

  const levelList =
    type && type.hasLevels && levels.length ? (
      <nav className="games-levels" aria-label="Levels">
        <h3 className="games-levels__title">{level ? "Levels" : "Choose a level"}</h3>
        <ol className="games-levels__list">
          {levels.map((item, index) => {
            const current = level && item.id === level.id;
            return (
              <li key={item.id} className="games-levels__item">
                <Link
                  to={levelPath(game.slug, item.id)}
                  className={"games-levels__link" + (current ? " is-current" : "")}
                  aria-current={current ? "true" : undefined}
                >
                  <span className="games-levels__number">{index + 1}</span>
                  <span className="games-levels__name">{item.name}</span>
                  {current ? <span className="games-levels__badge">Playing</span> : null}
                </Link>
              </li>
            );
          })}
        </ol>
      </nav>
    ) : null;

  let body;
  if (status === "loading") {
    body = (
      <div className="games-panel" role="status" aria-live="polite">
        <p className="games-panel__body">Loading game…</p>
      </div>
    );
  } else if (status === "not_found" || (status === "ready" && !type)) {
    body = (
      <div className="games-panel games-panel--empty" role="alert">
        <h2 className="games-panel__title">Game not found</h2>
        <p className="games-panel__body">This game isn’t available. It may have been removed.</p>
        <Link to="/games" className="btn btn-danger">
          Browse games
        </Link>
      </div>
    );
  } else if (status === "error") {
    body = (
      <div className="games-panel games-panel--error" role="alert">
        <h2 className="games-panel__title">Couldn’t load this game</h2>
        <p className="games-panel__body">Please check your connection and try again.</p>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Retry
        </button>
      </div>
    );
  } else {
    let shareSlot = null;
    if (type.shareable && completed && completed.round === play.round) {
      if (completed.proof !== undefined) {
        shareSlot = <GameShare key={"share-" + completed.round} token={play.token} proof={completed.proof} />;
      } else if (completed.completion) {
        shareSlot = <GameShare key={"share-" + completed.round} token={play.token} completion={completed.completion} />;
      } else {
        shareSlot = (
          <p className="games-share__note" data-testid="games-share-signed-out">
            Sign in to share your result to Community.
          </p>
        );
      }
    }
    let content;
    if (player) {
      const Player = player.Component;
      const extra = type.hasLevels
        ? { onRoundStart: handleRoundStart, onComplete: handleComplete, completionSlot: shareSlot }
        : {};
      if (type.serverSession) {
        extra.playToken = play.token;
        extra.playError = play.error;
        extra.playData = play.data;
        extra.backTo = levels.length > 1 ? { path: "/games/" + encodeURIComponent(game.slug), label: "Back to levels" } : { path: "/games", label: "Back to Games" };
      }
      content = <Player key={type.hasLevels ? "level-" + level.id : game.slug} {...player.props} {...extra} />;
    } else if (selection.invalid) {
      content = (
        <div className="games-panel games-panel--empty" role="alert">
          <h3 className="games-panel__title">This level isn’t available</h3>
          <p className="games-panel__body">It may have been removed or hidden. Choose another level below.</p>
        </div>
      );
    } else if (!levels.length) {
      content = (
        <div className="games-panel games-panel--empty" role="alert">
          <p className="games-panel__body">No levels are available right now.</p>
        </div>
      );
    } else {
      content = null;
    }

    body = (
      <article className="games-play" aria-labelledby="games-play-title">
        <header className="games-play__head">
          <h2 id="games-play-title" className="games-play__title">
            {game.title}
            {level ? <span className="games-play__level"> · {level.name}</span> : null}
          </h2>
          {game.description ? <p className="games-play__description">{game.description}</p> : null}
          {game.instructions ? (
            <p className="games-play__instructions">{game.instructions}</p>
          ) : null}
          {level && levels.length > 1 ? (
            <p className="games-levels__back">
              <Link to={"/games/" + encodeURIComponent(game.slug)}>← All levels</Link>
            </p>
          ) : null}
        </header>
        {level ? content : null}
        {levelList}
        {!level ? content : null}
      </article>
    );
  }

  return (
    <CommunityGamesShell
      title="Games"
      documentTitle={game ? `${game.title} — Games — Community` : "Games — Community"}
    >
      {backLink}
      {body}
    </CommunityGamesShell>
  );
}

export default GamePlayPage;
